import "./index.scss";

import { Component } from "react";
import PropTypes from "prop-types";
import ReactGA from "react-ga4";

import { debug } from "../../../utils/debug";

/**
 * Catches a crash anywhere below it and shows a friendly screen instead of a
 * blank page. React has no hook for this, so it has to be a class component.
 *
 * Two placements (see App.js and index.js):
 * - Around the routed pages, with `resetKey` set to the current URL: a crash in
 *   a game or menu keeps the top/bottom navigation alive, and navigating
 *   anywhere clears the error.
 * - Around the whole app, with no `resetKey`: the last line of defense for a
 *   crash during start-up or in the navigation itself. Its only way out is a
 *   reload, since the router may be what broke.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - What to protect
 * @param {string} [props.resetKey] - Clears the error whenever it changes
 * @param {Function} [props.onError] - Called once a crash is caught
 * @param {boolean} [props.fullPage] - Reload-only fallback for the app-level boundary
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    debug.error("Caught by ErrorBoundary:", error, info?.componentStack);
    ReactGA.event({
      category: "error",
      action: "crash",
      label: `${window.location.pathname}: ${error?.message ?? error}`,
      nonInteraction: true,
    });
    this.props.onError?.(error);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    const { fullPage } = this.props;
    return (
      <div className="errorboundary_container" role="alert">
        <div className="errorboundary_modal">
          <div className="errorboundary_modalcontentscontainer">
            <h1>Oops! Something went wrong.</h1>
            <div className="errorboundary_buttoncontainer">
              {!fullPage && (
                <button
                  type="button"
                  className="errorboundary_button hovercursor"
                  onClick={() => this.setState({ hasError: false })}
                >
                  Try again
                </button>
              )}
              {/* A full reload, not a router link: it also clears any game's
                  module-level state that may be what crashed. */}
              <button
                type="button"
                className="errorboundary_button hovercursor"
                onClick={() => window.location.assign("/")}
              >
                Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  resetKey: PropTypes.string,
  onError: PropTypes.func,
  fullPage: PropTypes.bool,
};
